import { Flex, Table, Typography } from 'antd';
import { dec } from '@app/shared';
import { MoneyInput } from '@/shared/ui/inputs';
import { useBreakpoint } from '@/shared/lib/hooks';
import type { AllocationRow } from './model';
import './AllocationGrid.mobile.css';

/** §7: сетка разнесения платежа/кассового ордера по документам — переиспользуется `PaymentWindow`
 * (оплата/возврат/обе стороны взаимозачёта) и `CashOrderWindow` (ПКО/РКО). На телефоне вместо
 * таблицы — карточки документов: «Долг», поле суммы на всю ширину и остаток после разнесения. */
export function AllocationGrid({
  title,
  remainderTitle = 'Долг',
  emptyText = 'Нет неоплаченных документов',
  rows,
  onChange,
}: {
  title: string;
  remainderTitle?: string;
  emptyText?: string;
  rows: AllocationRow[];
  onChange: (rows: AllocationRow[]) => void;
}) {
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <Flex vertical gap={8}>
        <Typography.Text strong>{title}</Typography.Text>
        {rows.length === 0 ? (
          <Typography.Text type="secondary">{emptyText}</Typography.Text>
        ) : (
          <div>
            {rows.map((row) => {
              // Остаток после разнесения — только подсказка: сумма в строке уже в канонической записи.
              const left = row.amount ? dec(row.debt).minus(dec(row.amount)) : null;
              return (
                <div key={row.documentId} className="m-card alloc-card">
                  <Flex justify="space-between" align="baseline" gap={8}>
                    <Typography.Text strong style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {row.label}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="tabular-nums" style={{ flex: '0 0 auto' }}>
                      {remainderTitle}: {row.debt}
                    </Typography.Text>
                  </Flex>
                  <label className="alloc-card__field">
                    <Typography.Text type="secondary">Сумма</Typography.Text>
                    <MoneyInput value={row.amount} onChange={(v) => onChange(rows.map((r) => (r.documentId === row.documentId ? { ...r, amount: v } : r)))} />
                  </label>
                  {left ? (
                    <Flex justify="space-between" gap={8}>
                      <Typography.Text type="secondary">Остаток после</Typography.Text>
                      <Typography.Text className="tabular-nums" type={left.isNegative() ? 'danger' : undefined}>
                        {left.toFixed(2)}
                      </Typography.Text>
                    </Flex>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Flex>
    );
  }

  return (
    <Flex vertical gap={4}>
      <Typography.Text style={{ fontSize: 12 }} strong>
        {title}
      </Typography.Text>
      <Table<AllocationRow>
        size="small"
        rowKey="documentId"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText }}
        columns={[
          { title: 'Документ', dataIndex: 'label', width: 120 },
          { title: remainderTitle, dataIndex: 'debt', width: 90, align: 'right' },
          {
            title: 'Сумма',
            dataIndex: 'amount',
            width: 110,
            align: 'right',
            render: (_v, row) => (
              <MoneyInput
                value={row.amount}
                onChange={(v) => onChange(rows.map((r) => (r.documentId === row.documentId ? { ...r, amount: v } : r)))}
              />
            ),
          },
        ]}
      />
    </Flex>
  );
}
